import { useState, useEffect, useRef, useCallback } from "react";
import { useSharedState, useAuth, useRoadmapList, createRoadmap, deleteRoadmap, renameRoadmap, supabase } from "./supabaseClient";
import {
  S, SPRINTS, buildSprints, LANES, EMPTY_SCHEDULE,
} from "./constants";
import { THEME_LIST, applyTheme } from "./themes";
import { ViewToggle, ProjectModal, InviteModal, UserManagementModal, RoadmapPicker, LaneManagerModal } from "./components";
import TimelineView from "./TimelineView";
import ListView from "./ListView";
import DigestView from "./DigestView";
import ReleaseCalendarView from "./ReleaseCalendarView";
import JiraView from "./JiraView";
import DashboardView from "./DashboardView";
import ExecutiveRoadmapView from "./ExecutiveRoadmapView";
import { updateExecutiveSettings } from "./executiveRoadmap";

// ─── Loading screen ───────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh",
      fontFamily:"'Inter',system-ui,-apple-system,sans-serif", background:"var(--bg-root)" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:24, marginBottom:12 }}>▦</div>
        <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)" }}>Loading Roadmap…</div>
        <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:4 }}>Syncing with database</div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
const VALID_VIEWS = ["timeline", "executive", "list", "digest", "release", "jira", "dashboard"];

export default function Roadmap() {
  const [activeRoadmapId, setActiveRoadmapId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("rm") || localStorage.getItem("activeRoadmapId") || "main";
  });
  const [themeId, setThemeId] = useState(() => {
    const saved = localStorage.getItem("themeId") || "modern";
    applyTheme(saved); // apply immediately — no flash
    return saved;
  });
  useEffect(() => { applyTheme(themeId); }, [themeId]);
  const switchTheme = (id) => { localStorage.setItem("themeId", id); setThemeId(id); };

  const switchRoadmap = (id) => {
    localStorage.setItem("activeRoadmapId", id);
    setActiveRoadmapId(id);
  };

  // ── Save status ────────────────────────────────────────────────────────────
  const [saveStatus,    setSaveStatus]    = useState("idle"); // "idle"|"saving"|"saved"|"error"
  const pendingSavesRef = useRef(0);
  const savedTimerRef   = useRef(null);

  const handleSaveStart = useCallback(() => {
    pendingSavesRef.current += 1;
    setSaveStatus("saving");
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const handleSaveEnd = useCallback((error) => {
    pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    if (pendingSavesRef.current === 0) {
      if (error) {
        setSaveStatus("error");
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 4000);
      } else {
        setSaveStatus("saved");
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2500);
      }
    }
  }, []);

  const [projects,   setProjects,  projLoaded,  projOk]  = useSharedState("projects",  [],                     activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [backlog,    setBacklog,   blLoaded,    blOk]    = useSharedState("backlog",   [],                     activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [completed,  setCompleted, compLoaded,  compOk]  = useSharedState("completed", [],                     activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [schedule,   setSchedule,  schLoaded,   schOk]   = useSharedState("release_schedule", EMPTY_SCHEDULE,  activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [settings,   setSettings,  settLoaded,  settOk]  = useSharedState("settings",  { sprintYear: 2026 },   activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [history,    setHistory,   histLoaded,  histOk]  = useSharedState("history",   [],                     activeRoadmapId, handleSaveStart, handleSaveEnd);
  const [lanes,      setLanes,     lanesLoaded, lanesOk] = useSharedState("lanes",     LANES,                  activeRoadmapId, handleSaveStart, handleSaveEnd);

  // A column whose initial load attempt has resolved (loaded) but never
  // confirmed a successful DB read (loadOk false) is in the dangerous state:
  // edits update the screen but setAndSync silently blocks the write. Surface
  // it loudly and persistently. The hook keeps retrying the load in the
  // background, so this clears itself the moment a fetch succeeds.
  const saveBlocked =
    (projLoaded && !projOk) || (blLoaded && !blOk) || (compLoaded && !compOk) ||
    (schLoaded && !schOk)   || (settLoaded && !settOk) || (histLoaded && !histOk) ||
    (lanesLoaded && !lanesOk);

  const [roadmaps, , refetchRoadmaps] = useRoadmapList();

  const [backlogOpen,    setBacklogOpen]    = useState(true);
  const [dragging,       setDragging]       = useState(null);
  const [hoverCell,      setHoverCell]      = useState(null);
  const [addForm,        setAddForm]        = useState(false);
  const [editTarget,     setEditTarget]     = useState(null);
  const [editStatus,     setEditStatus]     = useState(null);
  const [view,           setView]           = useState(() => {
    const v = new URLSearchParams(window.location.search).get("view");
    return VALID_VIEWS.includes(v) ? v : "timeline";
  });
  const [roadmapFilter,  setRoadmapFilter]  = useState(() => {
    const v = new URLSearchParams(window.location.search).get("filter");
    return ["ai","revenue","core"].includes(v) ? v : "all";
  });
  const [listFilter, setListFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      lane:   p.get("lfl")  || "all",
      driver: p.get("lfd")  || "all",
      status: p.get("lfs")  || "all",
      risk:   p.get("lfr")  || "all",
      stage:  p.get("lfsi") || "all",
    };
  });
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [showAbout,      setShowAbout]      = useState(false);
  const [showHistory,    setShowHistory]    = useState(false);
  const [notifOpen,      setNotifOpen]      = useState(false);
  const [lastSeenTs,     setLastSeenTs]     = useState(null); // newest history ts the user has acknowledged
  const seenSnapshotRef  = useRef(null);                      // lastSeenTs captured when the panel was opened (for unread highlight)
  const { session, authLoaded, needsPasswordSet, setNeedsPasswordSet } = useAuth();
  const [loginError,       setLoginError]      = useState("");
  const [loginEmail,       setLoginEmail]       = useState("");
  const [loginPassword,    setLoginPassword]    = useState("");
  const [newPassword,      setNewPassword]      = useState("");
  const [confirmPassword,  setConfirmPassword]  = useState("");
  const [setPasswordError, setSetPasswordError] = useState("");
  const [showInvite,       setShowInvite]       = useState(false);
  const [showManageUsers,  setShowManageUsers]  = useState(false);
  const [showLaneManager,  setShowLaneManager]  = useState(false);
  const [showIdeaModal,    setShowIdeaModal]    = useState(false);
  const [ideaText,         setIdeaText]         = useState("");
  const [forgotMode,       setForgotMode]       = useState(false);
  const [resetEmail,       setResetEmail]       = useState("");
  const [resetError,       setResetError]       = useState("");
  const [resetSent,        setResetSent]        = useState(false);
  const [pdfSubOpen,       setPdfSubOpen]       = useState(false);
  const [pdfExporting,     setPdfExporting]     = useState(false);
  const [jiraSyncing,      setJiraSyncing]      = useState(false);
  const contentRef = useRef(null);

  // Sync view, filters, and roadmap to URL so pages are deep-linkable
  useEffect(() => {
    const params = new URLSearchParams();
    if (view !== "timeline")       params.set("view",  view);
    if (activeRoadmapId !== "main") params.set("rm",   activeRoadmapId);
    if (roadmapFilter !== "all")   params.set("filter", roadmapFilter);
    if (listFilter.lane   !== "all") params.set("lfl",  listFilter.lane);
    if (listFilter.driver !== "all") params.set("lfd",  listFilter.driver);
    if (listFilter.status !== "all") params.set("lfs",  listFilter.status);
    if (listFilter.risk   !== "all") params.set("lfr",  listFilter.risk);
    if (listFilter.stage  !== "all") params.set("lfsi", listFilter.stage);
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }, [view, activeRoadmapId, roadmapFilter, listFilter]);

  // Warn before leaving while a save is still in-flight
  useEffect(() => {
    const handler = (e) => {
      if (saveStatus === "saving") { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveStatus]);

  const role            = session?.user?.app_metadata?.role;
  const canEdit         = role === "write" || role === "super_admin";
  const canEditCalendar = canEdit || role === "marketing";
  const canInvite       = role === "super_admin";

  const exportPDF = async (targetView) => {
    setMenuOpen(false);
    setPdfSubOpen(false);
    const prevView = view;
    if (targetView !== view) {
      setView(targetView);
      await new Promise(r => setTimeout(r, 500));
    }
    setPdfExporting(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const { jsPDF } = await import("jspdf");
      const el = contentRef.current;
      const prevOverflow = el.style.overflow;
      const prevHeight = el.style.height;
      el.style.overflow = "visible";
      el.style.height = "auto";
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, allowTaint: true, logging: false });
      el.style.overflow = prevOverflow;
      el.style.height = prevHeight;
      const imgData = canvas.toDataURL("image/png");
      const isLandscape = canvas.width > canvas.height;
      const pdf = new jsPDF({ orientation: isLandscape ? "landscape" : "portrait", unit: "mm", format: "a3" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgH = pdfW / (canvas.width / canvas.height);
      let remaining = imgH - pdfH;
      let yPos = 0;
      pdf.addImage(imgData, "PNG", 0, yPos, pdfW, imgH);
      while (remaining > 0) {
        yPos -= pdfH;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, yPos, pdfW, imgH);
        remaining -= pdfH;
      }
      const roadmapName = roadmaps.find(r => r.id === activeRoadmapId)?.name || "Roadmap";
      const labels = { timeline: "Timeline", digest: "Digest", release: "Release Calendar" };
      pdf.save(`${roadmapName} - ${labels[targetView]}.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      alert("PDF export failed. Check the console for details.");
    } finally {
      setPdfExporting(false);
      if (targetView !== prevView) setView(prevView);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });
    if (error) setLoginError(error.message);
  };
  const handleLogout = () => {
    supabase.auth.signOut();
    setAddForm(false);
    setEditTarget(null);
    setEditStatus(null);
  };
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: window.location.origin,
    });
    if (error) { setResetError(error.message); } else { setResetSent(true); }
  };

  const handleInvite = async (email, inviteRole) => {
    if (!session || !session.access_token) throw new Error("Authentication required");
    const { error } = await supabase.functions.invoke("invite-user", {
      body: { email, role: inviteRole },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (error) {
      let msg = error.message || "Failed to send invite";
      try { const body = await error.context?.json(); msg = body?.error || msg; } catch {}
      throw new Error(msg);
    }
  };

  const handleManageUsers = async (operation, userId, role, email) => {
    if (!session || !session.access_token) throw new Error("Authentication required");
    const { data, error } = await supabase.functions.invoke("manage-users", {
      body: { operation, userId, role, email },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (error) {
      let msg = error.message || "Operation failed";
      try { const body = await error.context?.json(); msg = body?.error || body?.message || msg; } catch {}
      throw new Error(msg);
    }
    return data;
  };

  const handleCreateRoadmap = async (name) => {
    const id = await createRoadmap(name, lanes);
    refetchRoadmaps();
    return id;
  };

  const handleDeleteRoadmap = async (id) => {
    await deleteRoadmap(id);
    if (id === activeRoadmapId) switchRoadmap("main");
  };

  const handleRenameRoadmap = async (id, name) => {
    await renameRoadmap(id, name);
    refetchRoadmaps();
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSetPasswordError("");
    if (newPassword.length < 8) { setSetPasswordError("Password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setSetPasswordError("Passwords do not match."); return; }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setSetPasswordError(error.message);
    } else {
      window.history.replaceState(null, "", window.location.pathname);
      setNeedsPasswordSet(false);
    }
  };

  // Rebuild SPRINTS if year changes
  const sprintYear     = settings?.sprintYear     || 2026;
  const scoringMethod  = settings?.scoringMethod  || "score";
  // eslint-disable-next-line no-import-assign
  SPRINTS.splice(0, SPRINTS.length, ...buildSprints(sprintYear));

  const visibleSprints = [0, SPRINTS.length - 1];
  const sprintEndLabel = SPRINTS[SPRINTS.length - 1]?.start.toLocaleString("en-US", { month:"short", year:"numeric" });
  const dataLoaded = projLoaded && blLoaded && compLoaded && schLoaded && settLoaded && histLoaded && lanesLoaded;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const logHistory = (description, type = "change", extra = {}) => {
    const user = session?.user?.email;
    const entry = { id: Date.now(), ts: new Date().toISOString(), description, type, user, ...extra };
    setHistory(prev => [entry, ...(Array.isArray(prev) ? prev : [])].slice(0, 100));
  };

  // ── Change notifications ─────────────────────────────────────────────────────
  // Derives entirely from the live `history` array (already realtime-synced), so
  // no DB/sync changes are needed. A change is "unread" if it was made by someone
  // else and is newer than the last entry this user acknowledged.
  const myEmail = session?.user?.email;
  const notifSeenKey = `notif_seen_${activeRoadmapId}_${myEmail || "anon"}`;

  // Initialize the seen-marker per roadmap. First time on a roadmap, treat all
  // existing history as already seen so the badge doesn't light up on day one.
  useEffect(() => {
    if (!histLoaded) return;
    const stored = localStorage.getItem(notifSeenKey);
    if (stored) {
      setLastSeenTs(stored);
    } else {
      const newest = (Array.isArray(history) && history[0]?.ts) || new Date().toISOString();
      setLastSeenTs(newest);
      localStorage.setItem(notifSeenKey, newest);
    }
    // history intentionally excluded — this only seeds the marker, it must not
    // reset every time a new change streams in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoadmapId, histLoaded, myEmail]);

  const othersChanges = (Array.isArray(history) ? history : []).filter(e => e.user && e.user !== myEmail);
  const unreadCount   = othersChanges.filter(e => !lastSeenTs || e.ts > lastSeenTs).length;

  const toggleNotif = () => {
    if (notifOpen) { setNotifOpen(false); return; }
    seenSnapshotRef.current = lastSeenTs;            // remember what was unread for highlighting
    const newest = (Array.isArray(history) && history[0]?.ts) || new Date().toISOString();
    setLastSeenTs(newest);                           // mark everything seen → clears the badge
    localStorage.setItem(notifSeenKey, newest);
    setNotifOpen(true);
  };

  const openEdit = (p) => {
    if (!canEdit) return;
    const status = completed.find((x) => x.id === p.id) ? "completed"
                 : backlog.find((x) => x.id === p.id)   ? "backlog"
                 : (p.status || "scheduled");
    setEditTarget(p);
    setEditStatus(status);
  };

  const onDropCell = (e, laneId, sprintIdx) => {
    e.preventDefault();
    if (!canEdit || !dragging) return;
    const { project, source } = dragging;
    if (source === "backlog") {
      logHistory(`Scheduled "${project.name}" → Sprint ${sprintIdx + 1}`, "schedule");
      setBacklog((b) => b.filter((p) => p.id !== project.id));
      setProjects((ps) => [...ps, { ...project, lane: laneId, sprint: sprintIdx, status: "scheduled" }]);
    } else {
      const c = [];
      if (project.sprint !== sprintIdx) c.push(`Sprint ${project.sprint + 1} → ${sprintIdx + 1}`);
      if (project.lane !== laneId) c.push("lane changed");
      if (c.length) logHistory(`Moved "${project.name}": ${c.join(" · ")}`, "move");
      setProjects((ps) => ps.map((p) => p.id === project.id ? { ...p, lane: laneId, sprint: sprintIdx } : p));
    }
    setDragging(null); setHoverCell(null);
  };

  const onDropBacklog = (e) => {
    e.preventDefault();
    if (!canEdit || !dragging || dragging.source === "backlog") return;
    const { project } = dragging;
    logHistory(`Moved to backlog: "${project.name}"`, "unschedule");
    setProjects((ps) => ps.filter((p) => p.id !== project.id));
    const { sprint, ...rest } = project;
    setBacklog((b) => [...b, rest]);
    setDragging(null);
  };

  const markComplete = (p) => {
    if (!canEdit) return;
    setProjects((ps) => ps.filter((x) => x.id !== p.id));
    setBacklog((b)   => b.filter((x) => x.id !== p.id));
    if (!completed.find((x) => x.id === p.id)) {
      setCompleted((c) => [...c, { ...p, status: "completed", completedAt: Date.now() }]);
      logHistory(`Marked complete: ${p.name}`, "complete");
    }
  };

  const restoreProject = (p) => {
    if (!canEdit) return;
    setCompleted((c) => c.filter((x) => x.id !== p.id));
    const { completedAt, ...rest } = p;
    if (rest.sprint !== undefined) setProjects((ps) => [...ps, { ...rest, status: rest.status === "completed" ? "scheduled" : (rest.status || "scheduled") }]);
    else setBacklog((b) => [...b, { ...rest, status: "backlog" }]);
    logHistory(`Restored from completed: "${p.name}"`, "restore");
  };

  const reorderBacklog = (fromIdx, toIdx) => {
    if (!canEdit) return;
    if (fromIdx === toIdx || fromIdx === toIdx - 1) return;
    setBacklog((b) => {
      const next = [...b];
      const [item] = next.splice(fromIdx, 1);
      const insertAt = toIdx > fromIdx ? toIdx - 1 : toIdx;
      next.splice(insertAt, 0, item);
      return next;
    });
  };

  const removeProject = (id) => {
    if (!canEdit) return;
    const target = [...projects, ...backlog, ...completed].find(p => p.id === id);
    logHistory(`Deleted: "${target?.name || id}"`, "delete", { deletedProject: target });
    setProjects((ps)  => ps.filter((p) => p.id !== id));
    setBacklog((b)    => b.filter((p) => p.id !== id));
    setCompleted((c)  => c.filter((p) => p.id !== id));
  };

  const handleRemoveFromList = (p) => { removeProject(p.id); };

  const handleBulkUpdate = (ids, changes) => {
    if (!canEdit || ids.length === 0) return;
    const patch = (list) => list.map((p) => ids.includes(p.id) ? { ...p, ...changes } : p);
    setProjects(patch); setBacklog(patch); setCompleted(patch);
    logHistory(`Bulk updated ${ids.length} project${ids.length !== 1 ? "s" : ""}: ${Object.keys(changes).join(", ")}`, "edit");
  };

  const handleBulkMoveToBacklog = (ids) => {
    if (!canEdit || ids.length === 0) return;
    setProjects((ps) => {
      const toMove = ps.filter((p) => ids.includes(p.id));
      setBacklog((b) => [...b, ...toMove.map(({ sprint: _s, ...rest }) => ({ ...rest, status: "backlog" }))]);
      return ps.filter((p) => !ids.includes(p.id));
    });
    logHistory(`Moved ${ids.length} project${ids.length !== 1 ? "s" : ""} to backlog`, "unschedule");
  };

  const quickUpdateProject = (id, changes) => {
    if (!canEdit) return;
    if (changes.score !== undefined) {
      const target = [...projects, ...backlog, ...completed].find(p => p.id === id);
      if (target) logHistory(`Rescored "${target.name}": ${target.score || "—"} → ${changes.score}`, "rescore");
    }
    const applyUpdate = (list) => list.map(p => p.id === id ? { ...p, ...changes } : p);
    setProjects(applyUpdate);
    setBacklog(applyUpdate);
    setCompleted(applyUpdate);
  };

  const handleSave = (form, newStatus) => {
    if (!canEdit) return;
    const updatedProject = { ...editTarget, ...form };

    if (editTarget) {
      const t = editTarget;
      const changes = [];
      if (form.name !== t.name) changes.push(`renamed "${t.name}" → "${form.name}"`);
      if ((form.sprint ?? -1) !== (t.sprint ?? -1)) changes.push(`Sprint ${(t.sprint ?? 0) + 1} → ${(form.sprint ?? 0) + 1}`);
      if (form.sprints !== t.sprints) changes.push(`duration ${t.sprints} → ${form.sprints} sprints`);
      if (form.lane !== t.lane) changes.push(`lane changed`);
      if ((form.score || 0) !== (t.score || 0)) changes.push(`score ${t.score || "—"} → ${form.score || "—"}`);
      if (newStatus !== (t.status || "scheduled")) changes.push(`status → ${newStatus}`);
      const hasContactChange = ["pmName","engLead","poName"].some(k => (form[k]||"") !== (t[k]||""));
      if (hasContactChange) changes.push("contacts updated");
      let type = "edit";
      if ((form.sprint ?? -1) !== (t.sprint ?? -1) || form.lane !== t.lane) type = "move";
      else if (form.sprints !== t.sprints) type = "resize";
      else if ((form.score || 0) !== (t.score || 0)) type = "rescore";
      else if (form.name !== t.name) type = "rename";
      const desc = changes.length ? `${form.name}: ${changes.join(" · ")}` : `Edited: ${form.name}`;
      logHistory(desc, type);
      setProjects((ps)  => ps.filter((x) => x.id !== editTarget.id));
      setBacklog((b)    => b.filter((x) => x.id !== editTarget.id));
      setCompleted((c)  => c.filter((x) => x.id !== editTarget.id));

      if (newStatus === "completed") {
        setCompleted((c) => [...c, { ...updatedProject, completedAt: Date.now() }]);
      } else if (newStatus === "backlog") {
        const { sprint, ...rest } = updatedProject;
        setBacklog((b) => [...b, { ...rest, status: "backlog" }]);
      } else {
        const updated = { ...updatedProject, status: newStatus };
        if (updated.sprint !== undefined) setProjects((ps) => [...ps, updated]);
        else setBacklog((b) => [...b, { ...updated }]);
      }
      setEditTarget(null); setEditStatus(null);

      // Auto-sync Jira status when jiraEpicKey is set or changed
      const oldKey = editTarget?.jiraEpicKey?.trim();
      const newKey = form.jiraEpicKey?.trim();
      if (newKey && newKey !== oldKey) {
        supabase.functions.invoke("sync-jira", { body: { epicKeys: [newKey] } })
          .then(({ data, error }) => {
            if (!error && data?.results?.[newKey] && !data.results[newKey].error) {
              const r = data.results[newKey];
              const patch = (list) => list.map((p) =>
                p.jiraEpicKey?.trim() === newKey ? { ...p, jiraStatus: r.status, jiraStatusCategory: r.statusCategory } : p
              );
              setProjects(patch); setBacklog(patch); setCompleted(patch);
            }
          }).catch(() => {});
      }
    } else {
      logHistory(`Added: ${form.name}`, "add");
      setBacklog((b) => [...b, { id:`p${Date.now()}`, ...form, status:'backlog' }]);
      setAddForm(false);
    }
  };

  const addJiraToBacklog = (epic, laneId) => {
    const newProj = {
      id: `p${Date.now()}`,
      name: epic.summary,
      lane: laneId,
      driver: "core",
      score: 0,
      value: "",
      sprints: 2,
      points: 8,
      confluenceUrl: "",
      figmaUrl: "",
      aiInitiative: false,
      atRisk: false,
      imageUrl: "",
      notes: "",
      businessArea: "",
      pmName: "",
      engLead: "",
      poName: "",
      jiraEpicKey: epic.key,
      jiraStatus: epic.status,
      jiraStatusCategory: epic.statusCategory,
      status: "backlog",
    };
    logHistory(`Added "${epic.summary}" from Jira to backlog`, "add");
    setBacklog((bl) => [...bl, newProj]);
  };

  const syncJira = async () => {
    const allProjects = [...projects, ...backlog, ...completed];
    const keyed = allProjects.filter(p => p.jiraEpicKey?.trim());
    if (keyed.length === 0) { alert("No projects have a Jira Epic Key set. Edit a project and add one."); return; }
    setJiraSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-jira", {
        body: { epicKeys: keyed.map(p => p.jiraEpicKey.trim()) },
      });
      if (error) throw error;
      const { results } = data;
      const applyJira = (list) => list.map(p => {
        if (!p.jiraEpicKey) return p;
        const r = results[p.jiraEpicKey.trim()];
        if (!r || r.error) return p;
        return { ...p, jiraStatus: r.status, jiraStatusCategory: r.statusCategory };
      });
      setProjects(applyJira);
      setBacklog(applyJira);
      setCompleted(applyJira);
      logHistory(`Synced Jira status for ${keyed.length} epic${keyed.length !== 1 ? "s" : ""}`, "import");
    } catch (err) {
      alert("Jira sync failed: " + (err.message || String(err)));
    } finally {
      setJiraSyncing(false);
    }
  };

  if (!authLoaded) return <LoadingScreen />;

  if (needsPasswordSet && !session) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh",
      fontFamily:"'Inter',system-ui,-apple-system,sans-serif", background:"var(--bg-root)" }}>
      <div style={{ ...S.modal, width:380, textAlign:"center" }}>
        <div style={{ fontSize:24, marginBottom:8 }}>▦</div>
        <div style={S.modalTitle}>Invite Link Expired</div>
        <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:20, marginTop:-10 }}>
          This invite link has already been used or has expired. Please ask an admin to send you a new invite.
        </div>
        <button onClick={() => setNeedsPasswordSet(false)} style={S.btnGhost}>Back to Login</button>
      </div>
    </div>
  );

  if (needsPasswordSet && session) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh",
      fontFamily:"'Inter',system-ui,-apple-system,sans-serif", background:"var(--bg-root)" }}>
      <div style={{ ...S.modal, width:380 }}>
        <div style={{ fontSize:24, textAlign:"center", marginBottom:8 }}>▦</div>
        <div style={S.modalTitle}>Set Your Password</div>
        <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:20, marginTop:-10 }}>
          Welcome! Choose a password to access the PDI Super Roadmap Tool.
        </div>
        <form onSubmit={handleSetPassword} style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <label style={S.label}>New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              style={S.input} autoFocus placeholder="Min. 8 characters" />
          </div>
          <div>
            <label style={S.label}>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              style={S.input} />
          </div>
          {setPasswordError && <div style={{ color:"var(--color-danger,#DC2626)", fontSize:12 }}>{setPasswordError}</div>}
          <button type="submit" style={S.btnPrimary}>Set Password &amp; Continue</button>
        </form>
      </div>
    </div>
  );

  if (!session) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh",
      fontFamily:"'Inter',system-ui,-apple-system,sans-serif", background:"var(--bg-root)" }}>
      <div style={{ ...S.modal, width:360 }}>
        <div style={{ fontSize:24, textAlign:"center", marginBottom:8 }}>▦</div>
        <div style={{ fontSize:18, fontWeight:800, color:"var(--text-primary)", marginBottom:4, textAlign:"center" }}>PDI Super Roadmap Tool</div>

        {resetSent ? (
          <>
            <div style={{ fontSize:12, color:"var(--color-success,#16A34A)", fontWeight:700, margin:"16px 0 8px", textAlign:"center" }}>
              Check your email for a reset link.
            </div>
            <button onClick={() => { setResetSent(false); setForgotMode(false); }} style={{ ...S.btnGhost, width:"100%" }}>
              Back to Sign in
            </button>
          </>
        ) : forgotMode ? (
          <>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:20, textAlign:"center" }}>
              Enter your email and we'll send a reset link.
            </div>
            <form onSubmit={handleResetPassword} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label htmlFor="reset-email" style={S.label}>Email</label>
                <input id="reset-email" type="email" autoComplete="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                  style={S.input} autoFocus placeholder="you@company.com" />
              </div>
              {resetError && <div role="alert" style={{ color:"var(--color-danger,#DC2626)", fontSize:12 }}>{resetError}</div>}
              <button type="submit" style={S.btnPrimary}>Send Reset Link</button>
              <button type="button" onClick={() => setForgotMode(false)} style={{ ...S.btnGhost, width:"100%", marginTop:2 }}>
                Back to Sign in
              </button>
            </form>
          </>
        ) : (
          <>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:20, textAlign:"center" }}>Sign in to continue</div>
            <form onSubmit={handleLogin} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label htmlFor="login-email" style={S.label}>Email</label>
                <input id="login-email" type="email" autoComplete="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                  style={S.input} autoFocus placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="login-password" style={S.label}>Password</label>
                <input id="login-password" type="password" autoComplete="current-password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                  style={S.input} />
              </div>
              {loginError && <div role="alert" style={{ color:"var(--color-danger,#DC2626)", fontSize:12 }}>{loginError}</div>}
              <button type="submit" style={S.btnPrimary}>Sign in</button>
              <button type="button" onClick={() => setForgotMode(true)}
                style={{ background:"none", border:"none", cursor:"pointer", fontSize:11,
                  color:"var(--text-muted)", fontFamily:"inherit", marginTop:4, padding:0 }}>
                Forgot password?
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );

  if (!dataLoaded) return <LoadingScreen />;

  return (
    <div style={S.root}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:22, color:"var(--accent)", lineHeight:1, flexShrink:0 }}>▦</span>
            <div>
              <div className="gradient-title" style={S.title}>PDI Super Roadmap</div>
              <div style={S.subtitle}>{sprintYear}–{sprintEndLabel}</div>
            </div>
          </div>
          <RoadmapPicker
            roadmaps={roadmaps}
            activeId={activeRoadmapId}
            canEdit={canEdit}
            onSwitch={switchRoadmap}
            onCreate={handleCreateRoadmap}
            onDelete={handleDeleteRoadmap}
            onRename={handleRenameRoadmap}
          />
        </div>
        <div style={S.headerRight}>
          <ViewToggle view={view} onChange={setView} />

          {view === "timeline" && (
            <select value={roadmapFilter} onChange={(e) => setRoadmapFilter(e.target.value)}
              aria-label="Filter projects"
              style={{
                background:  "var(--btn-ghost-bg)",
                color:       roadmapFilter !== "all" ? "var(--accent)" : "var(--btn-ghost-txt)",
                border:      `1px solid ${roadmapFilter !== "all" ? "var(--accent)" : "var(--btn-ghost-border)"}`,
                borderRadius: 8, padding:"7px 12px", fontSize:12, fontWeight:700,
                fontFamily:"inherit", cursor:"pointer",
              }}>
              <option value="all">All Projects</option>
              <option value="ai">🤖 AI Only</option>
              <option value="revenue">Revenue Only</option>
              <option value="core">Core Only</option>
            </select>
          )}

          {canEdit && <button style={S.btnPrimary} onClick={() => setAddForm(true)}>+ Add Project</button>}
          <button style={S.btnGhost} onClick={() => { setIdeaText(""); setShowIdeaModal(true); }}>💡 Idea</button>
          <button onClick={handleLogout} style={S.btnGhost} title={session.user.email}>Logout</button>

          {/* Change notifications */}
          {session && (
            <div style={{ position:"relative" }}>
              <button onClick={toggleNotif} title="Recent changes from others" style={{
                ...S.btnGhost, padding:"7px 10px", fontSize:16, lineHeight:1, position:"relative",
                background: notifOpen ? "var(--bg-hover)" : "var(--btn-ghost-bg)",
              }}>
                🔔
                {unreadCount > 0 && (
                  <span style={{
                    position:"absolute", top:-3, right:-3, minWidth:16, height:16, padding:"0 4px",
                    borderRadius:8, background:"#EF4444", color:"#fff", fontSize:10, fontWeight:800,
                    display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1,
                    border:"2px solid var(--bg-card)",
                  }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                )}
              </button>

              {notifOpen && (
                <>
                  <div onClick={() => setNotifOpen(false)} style={{ position:"fixed", inset:0, zIndex:49 }} />
                  <div className="menu-pop" style={{
                    position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:50,
                    background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10,
                    boxShadow:"0 16px 40px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)",
                    width:340, maxHeight:440, overflow:"hidden", display:"flex", flexDirection:"column",
                  }}>
                    <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--border)" }}>
                      <div style={{ fontSize:12, fontWeight:800, color:"var(--text-primary)" }}>Changes from others</div>
                    </div>
                    <div style={{ flex:1, overflowY:"auto", padding:8, display:"flex", flexDirection:"column", gap:6 }}>
                      {othersChanges.length === 0 ? (
                        <div style={{ textAlign:"center", color:"var(--text-muted)", fontSize:12, padding:"28px 0" }}>
                          No changes from other users yet.
                        </div>
                      ) : othersChanges.slice(0, 30).map((entry) => {
                        const isUnread = !seenSnapshotRef.current || entry.ts > seenSnapshotRef.current;
                        return (
                          <div key={entry.id} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:"8px 10px",
                            background: isUnread ? "var(--bg-hover)" : "var(--bg-subtle)",
                            borderRadius:6, border:"1px solid var(--border)" }}>
                            <span style={{ width:7, height:7, borderRadius:"50%", marginTop:5, flexShrink:0,
                              background: isUnread ? "#EF4444" : "transparent" }} />
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{entry.description}</div>
                              <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:2 }}>
                                {new Date(entry.ts).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}
                                {entry.user && <span style={{ marginLeft:6 }}>· {entry.user}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding:"8px 12px", borderTop:"1px solid var(--border)", display:"flex", justifyContent:"flex-end" }}>
                      <button onClick={() => { setNotifOpen(false); setShowHistory(true); }}
                        style={{ ...S.btnGhost, fontSize:11, padding:"4px 10px" }}>View full history</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Hamburger menu */}
          <div style={{ position:"relative" }}>
            <button onClick={() => setMenuOpen(v => !v)} style={{
              ...S.btnGhost, padding:"7px 10px", fontSize:16, lineHeight:1,
              background: menuOpen ? "var(--bg-hover)" : "var(--btn-ghost-bg)",
            }}>☰</button>

            {menuOpen && (
              <>
                <div onClick={() => { setMenuOpen(false); setPdfSubOpen(false); }} style={{ position:"fixed", inset:0, zIndex:49 }} />
                <div className="menu-pop" style={{
                  position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:50,
                  background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10,
                  boxShadow:"0 16px 40px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)", minWidth:200, overflow:"hidden",
                }}>
                  {/* Theme picker */}
                  <div style={{ padding:"10px 14px 8px", borderBottom:"1px solid var(--border)" }}>
                    <div style={{ fontSize:9, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:7 }}>Theme</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {THEME_LIST.map(t => (
                        <button key={t.id} onClick={() => { switchTheme(t.id); }} title={t.name} style={{
                          width:28, height:28, borderRadius:6, border: themeId === t.id ? "2.5px solid var(--text-primary)" : "2px solid var(--border)",
                          cursor:"pointer", padding:2, background:"transparent", display:"flex", gap:2, overflow:"hidden",
                          boxShadow: themeId === t.id ? "0 0 0 1px var(--bg-card)" : "none",
                        }}>
                          {t.preview.map((c, i) => (
                            <div key={i} style={{ flex:1, background:c, borderRadius:2 }} />
                          ))}
                        </button>
                      ))}
                    </div>
                  </div>
                  {[
                    ...(session ? [{ label:"⬇ Export Data", action: () => {
                      const data = JSON.stringify({ projects, backlog, completed, schedule, lanes }, null, 2);
                      const blob = new Blob([data], { type:"application/json" });
                      const url = window.URL.createObjectURL(blob);
                      setTimeout(() => {
                        const a = document.createElement("a");
                        const roadmapName = roadmaps.find(r => r.id === activeRoadmapId)?.name || "Roadmap";
                        a.href = url; a.download = `${roadmapName} Backup.json`;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      }, 0);
                      setMenuOpen(false);
                    }}] : []),
                    ...(canInvite ? [{ label:"⬆ Import Data", action: () => {
                      document.getElementById("import-input").click(); setMenuOpen(false);
                    }}] : []),
                    ...(session ? [
                      { label:`📄 Export PDF ${pdfSubOpen ? "▾" : "▸"}`, action: () => setPdfSubOpen(v => !v) },
                      ...(pdfSubOpen ? [
                        { label:"Timeline",         action: () => exportPDF("timeline"), indent: true },
                        { label:"Digest",           action: () => exportPDF("digest"),   indent: true },
                        { label:"Release Calendar", action: () => exportPDF("release"),  indent: true },
                      ] : []),
                    ] : []),
                    { label:"↺ Reset",  danger: true, action: () => {
                      if (!canEdit) return;
                      setMenuOpen(false);
                      if (!window.confirm("Clear all projects and reset this roadmap to a blank state?")) return;
                      const yearStr = window.prompt(`Set sprint year (current: ${sprintYear}):`, String(sprintYear));
                      if (yearStr === null) return;
                      const newYear = parseInt(yearStr);
                      const y = (!isNaN(newYear) && newYear > 2020 && newYear < 2040) ? newYear : sprintYear;
                      setSettings(prev => ({ ...(prev || {}), sprintYear: y }));
                      SPRINTS.splice(0, SPRINTS.length, ...buildSprints(y));
                      setProjects([]);
                      setBacklog([]);
                      setCompleted([]);
                      setLanes(LANES);
                      setSchedule({ events: [], codeFreezes: [], notes: [] });
                      logHistory(`Reset to blank (year: ${y})`, "reset");
                    }, disabled: !canEdit },
                    ...(canEdit ? [
                      { label:"🏊 Manage Teams", action: () => { setShowLaneManager(true); setMenuOpen(false); } },
                      { label: scoringMethod === "rice" ? "🔢 Scoring: RICE (0–100)" : "⭐ Scoring: Weighted ★",
                        action: () => { setSettings(prev => ({ ...(prev||{}), scoringMethod: scoringMethod === "rice" ? "score" : "rice" })); setMenuOpen(false); },
                        highlight: scoringMethod === "rice" },
                    ] : []),
                    ...(canInvite ? [
                      { label:"✉ Invite User",   action: () => { setShowInvite(true);      setMenuOpen(false); } },
                      { label:"👥 Manage Users", action: () => { setShowManageUsers(true); setMenuOpen(false); } },
                    ] : []),
                    ...(canEdit ? [
                      { label: jiraSyncing ? "🔄 Syncing Jira…" : "🔄 Sync Jira Status",
                        action: () => { setMenuOpen(false); syncJira(); },
                        disabled: jiraSyncing },
                    ] : []),
                    { divider: true },
                    ...(session ? [{ label:"🕐 History", action: () => { setShowHistory(true); setMenuOpen(false); }}] : []),
                    { label:"ℹ About",   action: () => { setShowAbout(true);   setMenuOpen(false); }},
                  ].map((item, i) => item.divider
                    ? <div key={i} style={{ height:1, background:"var(--border)", margin:"4px 0" }} />
                    : (
                      <button key={i} onClick={item.action} style={{
                        display:"block", width:"100%", textAlign:"left",
                        padding: item.indent ? "7px 16px 7px 28px" : "10px 16px",
                        fontSize: item.indent ? 12 : 13, fontWeight:600,
                        background: item.highlight ? "var(--sprint-next-bg)" : "transparent",
                        border:"none", cursor:item.disabled?"not-allowed":"pointer",
                        fontFamily:"inherit", color: item.danger ? "var(--color-danger,#EF4444)" : item.highlight ? "var(--accent)" : item.indent ? "var(--text-secondary)" : "var(--text-primary)",
                        opacity: item.disabled ? 0.45 : 1,
                      }}
                      onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background="var(--bg-hover)" }}
                      onMouseLeave={e => { if (!item.disabled) e.currentTarget.style.background= item.highlight ? "var(--sprint-next-bg)" : "transparent" }}
                      >{item.label}</button>
                    )
                  )}
                </div>
              </>
            )}
          </div>

          {/* Hidden import input */}
          <input id="import-input" type="file" accept=".json" style={{ display:"none" }} onChange={(e) => {
            const file = e.target.files[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              try {
                const { projects:p, backlog:b, completed:c, schedule:sch, lanes:l } = JSON.parse(ev.target.result);
                const importedLanes = Array.isArray(l) ? l : lanes;
                const validLaneIds  = new Set(importedLanes.map(ln => ln.id));
                // Projects whose lane ID doesn't exist in the imported lanes drop to backlog
                const validProjects  = Array.isArray(p) ? p.filter(pr => validLaneIds.has(pr.lane)) : [];
                const orphaned       = Array.isArray(p) ? p.filter(pr => !validLaneIds.has(pr.lane))
                                         .map(({ sprint: _s, ...rest }) => ({ ...rest, status:"backlog" })) : [];
                const newBacklog     = [...(Array.isArray(b) ? b : []), ...orphaned];
                const newCompleted   = Array.isArray(c) ? c : completed;
                const newSchedule    = (sch && typeof sch === "object") ? sch : schedule;

                // Update React state immediately (optimistic)
                setProjects(validProjects);
                setBacklog(newBacklog);
                setCompleted(newCompleted);
                setSchedule(newSchedule);
                if (Array.isArray(l)) setLanes(importedLanes);
                logHistory("Imported roadmap data", "import");

                // Write ALL imported columns in one immediate Supabase call.
                // This avoids the debounce race where a page refresh mid-flight
                // abandons the individual per-column saves and reverts the data.
                const importedAt = new Date().toISOString();
                const payload = {
                  projects:         validProjects,
                  backlog:          newBacklog,
                  completed:        newCompleted,
                  release_schedule: newSchedule,
                  updated_at:       importedAt,
                };
                if (Array.isArray(l)) payload.lanes = importedLanes;
                supabase.from("roadmap_state")
                  .update(payload)
                  .eq("id", activeRoadmapId)
                  .then(({ error }) => { if (error) console.error("Import save error:", error); });

                if (orphaned.length) alert(`${orphaned.length} project(s) had unrecognized team assignments and were moved to the backlog.`);
              } catch { alert("Invalid JSON file."); }
            };
            reader.readAsText(file); e.target.value = "";
          }} />

        </div>
      </div>

      {/* PDF exporting overlay */}
      {pdfExporting && (
        <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"var(--bg-card)", padding:"24px 40px", borderRadius:12, fontSize:15, fontWeight:700, color:"var(--text-primary)", boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
            Generating PDF…
          </div>
        </div>
      )}

      {/* Main content */}
      <div ref={contentRef} style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
        {view === "executive" ? (
          <ExecutiveRoadmapView
            key={activeRoadmapId}
            projects={projects} backlog={backlog} completed={completed}
            board={settings.executiveBoard}
            onUpdate={canEdit && settOk ? action => setSettings(current => updateExecutiveSettings(current, action)) : undefined}
            onEdit={canEdit ? openEdit : undefined}
          />
        ) : view === "timeline" ? (
          <TimelineView
            projects={projects} backlog={backlog} completed={completed}
            roadmapFilter={roadmapFilter}
            backlogOpen={backlogOpen}
            onToggleBacklog={() => setBacklogOpen((v) => !v)}
            onDropCell={canEdit ? onDropCell : undefined} onDropBacklog={canEdit ? onDropBacklog : undefined}
            onRemove={canEdit ? removeProject : undefined} onEdit={canEdit ? openEdit : undefined}
            onRestore={canEdit ? restoreProject : undefined}
            onReorderBacklog={canEdit ? reorderBacklog : undefined}
            onQuickUpdate={canEdit ? quickUpdateProject : undefined}
            dragging={dragging} setDragging={setDragging}
            hoverCell={hoverCell} setHoverCell={setHoverCell}
            visibleSprints={visibleSprints}
            canEdit={canEdit}
            lanes={lanes}
            scoringMethod={scoringMethod}
          />
        ) : view === "digest" ? (
          <DigestView projects={projects} completed={completed} lanes={lanes} sprintYear={sprintYear} scoringMethod={scoringMethod} onEdit={canEdit ? openEdit : undefined} />
        ) : view === "release" ? (
          <ReleaseCalendarView
            schedule={schedule}
            setSchedule={canEditCalendar ? setSchedule : undefined}
            canEdit={canEditCalendar}
            sprintYear={sprintYear}
            roadmapName={roadmaps.find(r => r.id === activeRoadmapId)?.name || "Roadmap"}
          />
        ) : view === "jira" ? (
          <JiraView
            lanes={lanes}
            allProjects={[...projects, ...backlog, ...completed]}
            onAddToBacklog={canEdit ? addJiraToBacklog : undefined}
          />
        ) : view === "dashboard" ? (
          <DashboardView
            projects={projects} backlog={backlog} completed={completed}
            lanes={lanes} scoringMethod={scoringMethod}
          />
        ) : (
          <ListView
            projects={projects} backlog={backlog} completed={completed}
            onEdit={canEdit ? openEdit : undefined} onRemove={canEdit ? handleRemoveFromList : undefined}
            onMarkComplete={canEdit ? markComplete : undefined} onRestore={canEdit ? restoreProject : undefined}
            canEdit={canEdit} lanes={lanes} scoringMethod={scoringMethod}
            listFilter={listFilter} onListFilterChange={setListFilter}
            onBulkUpdate={canEdit ? handleBulkUpdate : undefined}
            onBulkMoveToBacklog={canEdit ? handleBulkMoveToBacklog : undefined}
          />
        )}
      </div>

      {/* Project modal */}
      {(addForm || editTarget) && (
        <ProjectModal
          initial={editTarget}
          currentStatus={editStatus}
          onSave={handleSave}
          onDelete={removeProject}
          onClose={() => { setAddForm(false); setEditTarget(null); setEditStatus(null); }}
          lanes={lanes}
          scoringMethod={scoringMethod}
          allProjects={[...projects, ...backlog, ...completed]}
        />
      )}

      {/* Invite modal */}
      {showInvite && (
        <InviteModal
          onInvite={handleInvite}
          onClose={() => setShowInvite(false)}
        />
      )}

      {/* Lane manager modal */}
      {showLaneManager && (
        <LaneManagerModal
          lanes={lanes}
          onSave={setLanes}
          onClose={() => setShowLaneManager(false)}
        />
      )}

      {/* User management modal */}
      {showManageUsers && (
        <UserManagementModal
          currentUserId={session?.user?.id}
          onManage={handleManageUsers}
          onClose={() => setShowManageUsers(false)}
          onInvite={() => { setShowManageUsers(false); setShowInvite(true); }}
        />
      )}

      {/* About modal */}
      {showAbout && (
        <div style={S.overlay} onClick={() => setShowAbout(false)}>
          <div style={{ ...S.modal, width:320, textAlign:"center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:24, marginBottom:8 }}>▦</div>
            <div style={{ fontSize:16, fontWeight:800, color:"var(--text-primary)", marginBottom:4 }}>PDI Super Roadmap Tool</div>
            <div style={{ fontSize:12, color:"var(--text-secondary)", marginBottom:2 }}>By John Swiderski</div>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:20 }}>Version 2.0</div>
            <button onClick={() => setShowAbout(false)} style={S.btnGhost}>Close</button>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div style={S.overlay} onClick={() => setShowHistory(false)}>
          <div style={{ ...S.modal, width:480, maxHeight:"70vh", display:"flex", flexDirection:"column" }} onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={S.modalTitle}>Recent Changes</div>
              <span style={{ fontSize:11, color:"var(--text-muted)" }}>Last {Math.min((history||[]).length, 100)} of 100 max</span>
            </div>
            <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
              {(!history || history.length === 0) ? (
                <div style={{ textAlign:"center", color:"var(--text-muted)", fontSize:13, padding:"32px 0" }}>No changes recorded yet.</div>
              ) : (history||[]).map((entry) => {
                const typeColors = {
                  add:"#16A34A", edit:"#2563EB", delete:"#EF4444", complete:"#0369A1",
                  reset:"#B45309", import:"#7C3AED", change:"#64748B",
                  move:"#0891B2", resize:"#7C3AED", rescore:"#F59E0B",
                  rename:"#2563EB", schedule:"#16A34A", unschedule:"#64748B", restore:"#0369A1",
                };
                const typeLabels = {
                  add:"Added", edit:"Edited", delete:"Deleted", complete:"Completed",
                  reset:"Reset", import:"Import", change:"Changed",
                  move:"Moved", resize:"Resized", rescore:"Rescored",
                  rename:"Renamed", schedule:"Scheduled", unschedule:"Backlogged", restore:"Restored",
                };
                const color = typeColors[entry.type] || "#64748B";
                const canRestore = entry.type === "delete" && entry.deletedProject;
                return (
                  <div key={entry.id} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"8px 10px",
                    background:"var(--bg-subtle)", borderRadius:6, border:"1px solid var(--border)" }}>
                    <span style={{ fontSize:9, fontWeight:800, padding:"2px 6px", borderRadius:3, background:color+"22",
                      color, border:`1px solid ${color}44`, whiteSpace:"nowrap", flexShrink:0, marginTop:1 }}>
                      {typeLabels[entry.type] || entry.type?.toUpperCase()}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{entry.description}</div>
                      <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:2 }}>
                        {new Date(entry.ts).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}
                        {entry.user && <span style={{ marginLeft:6 }}>· {entry.user}</span>}
                      </div>
                    </div>
                    {canRestore && canEdit && (
                      <button onClick={() => {
                        const p = entry.deletedProject;
                        if (p.sprint !== undefined) setProjects(ps => [...ps, { ...p, status: p.status || "scheduled" }]);
                        else setBacklog(b => [...b, { ...p, status: "backlog" }]);
                        logHistory(`Restored from history: "${p.name}"`, "restore");
                      }} style={{ ...S.btnGhost, fontSize:10, padding:"3px 8px", color:"#16A34A", borderColor:"#86EFAC", flexShrink:0, whiteSpace:"nowrap" }}>
                        ↺ Restore
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop:12, display:"flex", justifyContent:"flex-end" }}>
              <button onClick={() => setShowHistory(false)} style={S.btnGhost}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Idea submission modal */}
      {showIdeaModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:380 }}>
            <div style={S.modalTitle}>💡 Submit an Idea</div>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:16, marginTop:-6 }}>
              Your idea will be added to the bottom of the backlog.
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const name = ideaText.trim();
              if (!name) return;
              setBacklog(b => [...b, {
                id: `p${Date.now()}`,
                name,
                lane: lanes[0]?.id || "pay",
                driver: "core",
                score: 0, points: 1, sprints: 1,
                status: "backlog",
              }]);
              logHistory(`Idea submitted: ${name}`, "add");
              setShowIdeaModal(false);
            }} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={S.label}>What's your idea?</label>
                <input
                  type="text"
                  value={ideaText}
                  onChange={e => setIdeaText(e.target.value)}
                  style={S.input}
                  autoFocus
                  placeholder="Briefly describe the idea or feature…"
                  maxLength={120}
                />
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <button type="button" onClick={() => setShowIdeaModal(false)} style={S.btnGhost}>Cancel</button>
                <button type="submit" style={S.btnPrimary} disabled={!ideaText.trim()}>Add to Backlog</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Persistent data-safety banner: we couldn't confirm a load, so edits
          are NOT being saved. Stays up (no auto-dismiss) until a background
          retry succeeds. Reloading is the surest fix. */}
      {saveBlocked && (
        <div style={{
          position:"fixed", top:0, left:0, right:0, zIndex:2000,
          display:"flex", alignItems:"center", justifyContent:"center", gap:12,
          padding:"10px 16px", fontSize:13, fontWeight:700,
          fontFamily:"'Inter',system-ui,sans-serif",
          color:"#fff", background:"#DC2626",
          boxShadow:"0 2px 10px rgba(0,0,0,0.25)",
        }}>
          <span>⚠ Not connected — your changes are NOT being saved. Don’t rely on this session; reload to reconnect.</span>
          <button onClick={() => window.location.reload()} style={{
            padding:"4px 12px", borderRadius:6, border:"1px solid #ffffff88",
            background:"#ffffff22", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer",
          }}>Reload</button>
        </div>
      )}

      {/* Save status indicator */}
      {saveStatus !== "idle" && (
        <div className="save-toast" style={{
          position:"fixed", bottom:20, right:20, zIndex:1000,
          display:"flex", alignItems:"center", gap:7,
          padding:"8px 14px", borderRadius:10,
          fontSize:12, fontWeight:700, fontFamily:"'Inter',system-ui,sans-serif",
          background:"var(--bg-card)", border:"1px solid var(--border)",
          boxShadow:"0 8px 24px rgba(0,0,0,0.16), 0 2px 6px rgba(0,0,0,0.10)",
          ...(saveStatus === "saving" && { color:"var(--accent)", borderColor:"var(--border)" }),
          ...(saveStatus === "saved"  && { color:"#22C55E", borderColor:"#22C55E44" }),
          ...(saveStatus === "error"  && { color:"#EF4444", borderColor:"#EF444444" }),
        }}>
          {saveStatus === "saving" && <><span style={{ display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span> Saving…</>}
          {saveStatus === "saved"  && <>✓ Saved</>}
          {saveStatus === "error"  && <>✗ Save failed — check connection</>}
        </div>
      )}
    </div>
  );
}
